import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Award, ExternalLink, FileText, Handshake } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { MandalaCorner } from '../../components/luxury/MandalaCorner';
import { PatolaBorder } from '../../components/luxury/PatolaBorder';
import { api } from '../../App';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const TIERS = [
  { key: 'gold', label: 'Gold Tier', heading: 'Premier Partners', badge: 'bg-[hsl(var(--lux-gold))] text-white', cols: 'sm:grid-cols-2', bg: 'bg-white' },
  { key: 'silver', label: 'Silver Tier', heading: 'Supporting Partners', badge: 'bg-slate-400 text-white', cols: 'sm:grid-cols-2 lg:grid-cols-3', bg: 'bg-[hsl(var(--lux-parchment))]' },
  { key: 'bronze', label: 'Bronze Tier', heading: 'Community Partners', badge: 'bg-amber-700 text-white', cols: 'sm:grid-cols-2 lg:grid-cols-4', bg: 'bg-white' },
  { key: 'partner', label: 'Partners', heading: 'Friends of the Samaj', badge: 'bg-[hsl(var(--lux-royal))] text-white', cols: 'sm:grid-cols-2 lg:grid-cols-4', bg: 'bg-[hsl(var(--lux-parchment))]' },
];

const isPdf = (s) => /\.pdf$/i.test(s?.flyer_filename || '') || s?.flyer_mime === 'application/pdf';

function SponsorCard({ sponsor, tier }) {
  const flyer = sponsor.flyer_url ? `${BACKEND}${sponsor.flyer_url}` : null;
  const host = (() => { try { return new URL(sponsor.website_url).hostname.replace(/^www\./, ''); } catch { return sponsor.website_url; } })();
  return (
    <Card className={`lux-ornate-card h-full group hover:shadow-[var(--lux-shadow-2)] transition-shadow ${tier.key === 'gold' ? 'border-2 border-[hsl(var(--lux-gold))]' : 'border border-[hsl(var(--lux-gold)/0.4)]'}`} data-testid={`sponsor-card-${sponsor.sponsor_id}`}>
      <PatolaBorder />
      <CardContent className="p-5 flex flex-col h-full">
        <div className="rounded-xl overflow-hidden bg-[hsl(var(--lux-royal-soft))] border border-[hsl(var(--lux-gold)/0.3)] aspect-[4/3] flex items-center justify-center mb-4">
          {flyer && !isPdf(sponsor) ? (
            <img src={flyer} alt={`${sponsor.name} flyer`} className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform" loading="lazy" data-testid={`sponsor-flyer-${sponsor.sponsor_id}`} />
          ) : flyer ? (
            <a href={flyer} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-2 text-[hsl(var(--lux-royal))]"><FileText className="w-10 h-10" /><span className="text-sm font-medium">View flyer (PDF)</span></a>
          ) : (
            <div className="text-3xl font-bold text-[hsl(var(--lux-royal))]" style={{ fontFamily: 'Cormorant Garamond' }}>{sponsor.name.split(' ').map(w => w[0]).join('').slice(0, 3).toUpperCase()}</div>
          )}
        </div>
        <h3 className="text-xl font-bold text-[hsl(var(--lux-royal))]" style={{ fontFamily: 'Cormorant Garamond' }} data-testid={`sponsor-name-${sponsor.sponsor_id}`}>{sponsor.name}</h3>
        {sponsor.description && <p className="text-sm text-[hsl(var(--lux-ink)/0.7)] mt-1 flex-1">{sponsor.description}</p>}
        {sponsor.website_url && (
          <a href={sponsor.website_url} target="_blank" rel="noopener noreferrer sponsored" className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[hsl(var(--lux-gold))] hover:underline" data-testid={`sponsor-link-${sponsor.sponsor_id}`}>
            <ExternalLink className="w-4 h-4" /> {host}
          </a>
        )}
      </CardContent>
    </Card>
  );
}

export default function LuxurySponsors() {
  const [sponsors, setSponsors] = useState(null);
  const [error, setError] = useState('');
  const load = () => { setError(''); api.get('/api/sponsors').then(d => setSponsors(d.sponsors || [])).catch(e => { setError(e.message); setSponsors([]); }); };
  useEffect(() => { load(); }, []);

  const grouped = TIERS.map(t => ({ ...t, items: (sponsors || []).filter(s => (s.tier || 'gold') === t.key) })).filter(t => t.items.length);

  return (
    <div className="overflow-hidden">
      <section className="relative py-20 bg-gradient-to-br from-[hsl(var(--lux-royal-soft))] to-[hsl(var(--lux-ivory))] overflow-hidden">
        <MandalaCorner position="top-right" className="opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="text-5xl sm:text-6xl font-bold text-[hsl(var(--lux-royal))] mb-6" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="luxury-sponsors-title">
              Our Valued <span className="text-[hsl(var(--lux-gold))]">Sponsors</span>
            </h1>
            <p className="text-lg text-[hsl(var(--lux-ink)/0.8)] max-w-2xl mx-auto mb-8">
              We are grateful to our sponsors who support our mission to preserve and celebrate Gujarati heritage.
            </p>
            <div className="lux-gold-divider mx-auto max-w-md" />
          </motion.div>
        </div>
      </section>

      {sponsors === null && (
        <section className="py-16 bg-white"><div className="max-w-7xl mx-auto px-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{[0, 1, 2].map(i => <Skeleton key={i} className="h-64 rounded-xl" />)}</div></section>
      )}
      {error && (
        <section className="py-10"><div className="max-w-md mx-auto text-center"><p className="text-red-800">{error}</p><Button variant="outline" className="mt-3" onClick={load}>Retry</Button></div></section>
      )}
      {sponsors && sponsors.length === 0 && !error && (
        <section className="py-16 bg-white"><div className="max-w-xl mx-auto text-center px-4" data-testid="sponsors-empty">
          <Handshake className="w-12 h-12 mx-auto text-[hsl(var(--lux-gold))]" />
          <h2 className="mt-4 text-2xl font-bold text-[hsl(var(--lux-royal))]" style={{ fontFamily: 'Cormorant Garamond' }}>Sponsor announcements coming soon</h2>
          <p className="mt-2 text-[hsl(var(--lux-ink)/0.7)]">We are finalising our sponsor partnerships for the coming season. Interested in supporting the community?</p>
        </div></section>
      )}

      {grouped.map((tier, ti) => (
        <section key={tier.key} className={`py-16 ${tier.bg}`} data-testid={`sponsors-tier-${tier.key}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...fadeInUp} className="text-center mb-12">
              <Badge className={`mb-4 ${tier.badge} text-base px-5 py-1.5`}><Award className="w-4 h-4 mr-2" /> {tier.label}</Badge>
              <h2 className="text-3xl font-bold text-[hsl(var(--lux-royal))]" style={{ fontFamily: 'Cormorant Garamond' }}>{tier.heading}</h2>
            </motion.div>
            <div className={`grid ${tier.cols} gap-8 max-w-6xl mx-auto`}>
              {tier.items.map((s, idx) => (
                <motion.div key={s.sponsor_id} {...fadeInUp} transition={{ delay: Math.min(idx, 5) * 0.08 }}>
                  <SponsorCard sponsor={s} tier={tier} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="py-16 bg-[hsl(var(--lux-parchment))]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <Card className="lux-ornate-card border-2 border-[hsl(var(--lux-gold))] relative overflow-hidden">
              <MandalaCorner position="top-left" className="opacity-10" />
              <CardContent className="p-12 text-center relative">
                <h2 className="text-3xl font-bold text-[hsl(var(--lux-royal))] mb-4" style={{ fontFamily: 'Cormorant Garamond' }}>Become a Sponsor</h2>
                <p className="text-lg text-[hsl(var(--lux-ink)/0.7)] mb-8">Join our community of sponsors and help us preserve Gujarati heritage while gaining visibility among our engaged community.</p>
                <Link to="/contact"><Button className="bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))] px-8 py-6 text-lg" data-testid="luxury-sponsors-contact-button">Contact Us About Sponsorship</Button></Link>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
